// Note slice in redux toolkit uses immer so we mutate state
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { ReleaseDocument } from "@packages/commons";
import { useSelector } from "react-redux";

/**
 * Tracks which document is currently open. Its editor state lives in the
 * `documents-new` slice, keyed by `document.reference`. Kept separate because
 * `documents-new` is persisted and the selection should not be.
 */

/**
 * Usually a node from the release's document tree, but a freshly created file is
 * selected from the backend `Document` (with `reference` set to its id), so only
 * require the fields readers rely on.
 */
export type SelectedDocument = Pick<ReleaseDocument, "name" | "type" | "reference"> &
  Partial<Pick<ReleaseDocument, "version" | "deleted">>;

export interface SelectedDocumentState {
  document: SelectedDocument | null;
}

const initialState: SelectedDocumentState = {
  document: null,
};

const selectedDocumentSlice = createSlice({
  // reducer is mounted at `state.documents`, which other readers rely on
  name: "documents",
  initialState,
  reducers: {
    setSelectedDocument: (state, action: PayloadAction<SelectedDocument | null>) => {
      state.document = action.payload;
    },
  },
});

export const { setSelectedDocument } = selectedDocumentSlice.actions;

export const useSelectedDocument = () =>
  useSelector((state: { documents: SelectedDocumentState }) => state.documents.document);

export default selectedDocumentSlice.reducer;
